import { G } from '@mobily/ts-belt';
import { useMemo } from 'react';
import { SelectInput, SelectInputProps, useGetList } from 'react-admin';

import { isFullyManagedPlan } from '@Common/types/b2b/plans';
import {
  B2bCompanyPlan,
  B2bCompanyPlansQuery,
  B2bPlanInstrumentsCashSources,
  getPlanTypeName,
} from '@Common/types/bo/b2b/plans';

import { useResourceBasePath } from '@sh4res/bo-common/hooks';
import { isPerecoiPlanType } from '@sh4res/bo-common/utils/plans';

export type PlanSelectChoice = {
  id: number;
  name: string;
  type: B2bCompanyPlan['shares_plans'][0]['type'];
  instruments_universe: number[];
  is_cash_sources_per_instrument: boolean;
  instruments_cash_sources_libre: B2bPlanInstrumentsCashSources;
  isPerecoi: boolean;
  disabled?: boolean;
};

export type ExtraPlanChoice = {
  id: string;
  name: string;
  disabled?: boolean;
};

type PlanSelectInputProps = Omit<SelectInputProps, 'choices' | 'filter'> & {
  extraChoices?: ExtraPlanChoice[];
  onExtraPlanChange?: (plan: ExtraPlanChoice | undefined) => void;
  filter?: B2bCompanyPlansQuery['filter'];
  onPlanChange?: (plan: PlanSelectChoice | undefined) => void;
  disablePlan?: (plan: PlanSelectChoice) => boolean;
  requireVoluntaryDeposit?: boolean;
};

function hasContractFilter(contractId: number | number[] | null | undefined): boolean {
  if (Array.isArray(contractId)) {
    return contractId.length > 0;
  }

  return G.isNotNullable(contractId);
}

export const PlanSelectInput = ({
  filter,
  onPlanChange,
  disablePlan,
  requireVoluntaryDeposit = true,
  extraChoices = [],
  onExtraPlanChange,
  ...props
}: PlanSelectInputProps) => {
  const resourceBasePath = useResourceBasePath();

  const { data = [], isLoading } = useGetList<B2bCompanyPlan>(
    `${resourceBasePath}companies/plans`,
    {
      filter,
      sort: { field: 'created_at', order: 'DESC' },
      pagination: { page: 1, perPage: 100 },
    },
    {
      enabled: G.isNotNullable(filter?.company_id) && hasContractFilter(filter.b2b_contract_id),
    },
  );

  const choices = useMemo(
    () =>
      data.flatMap((plan) => {
        // Only plans accepting voluntary deposits are eligible for a voluntary payment
        if (requireVoluntaryDeposit && !plan.cash_sources.includes('voluntary_deposit')) {
          return [];
        }
        return (
          plan.shares_plans
            // ARTICLE plans are never selectable for voluntary payments
            .filter((sp) => !sp.type.includes('ARTICLE'))
            .map((sp) => {
              const name = getPlanTypeName(sp.type, plan.contract_type);
              const choice = {
                id: sp.id,
                name: `${name}${sp.plan_label ? ` - ${sp.plan_label}` : ''}`,
                type: sp.type,
                instruments_universe: sp.instruments_universe,
                // Managed plans have no per-instrument cash source restrictions
                is_cash_sources_per_instrument:
                  !isFullyManagedPlan(sp.type) && plan.is_cash_sources_per_instrument,
                instruments_cash_sources_libre: plan.instruments_cash_sources_libre,
                isPerecoi: isPerecoiPlanType(sp.type),
              };
              return {
                ...choice,
                disabled: disablePlan ? disablePlan(choice) : false,
              };
            })
        );
      }),
    [data, disablePlan, requireVoluntaryDeposit],
  );

  return (
    <SelectInput
      data-testid="BoExternalB2bVoluntaryPaymentPlanSelectInput"
      {...props}
      choices={[...choices, ...extraChoices]}
      isLoading={isLoading}
      onChange={(event) => {
        const selectedId = event.target.value;
        onPlanChange?.(choices.find((choice) => choice.id === selectedId));
        onExtraPlanChange?.(extraChoices.find((choice) => choice.id === selectedId));
      }}
    />
  );
};
